/* eslint-disable import-extensions/file-extension-required */
import type { AnalyticsEventPayload, UIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema/annotation';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { ExperienceStopStates } from '@confluence/experience-tracker/entry-points/ExperienceStopStates.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes';
import {
	type AnalyticsSource,
	CommentType,
} from '@confluence/comments-util/entry-points/analytics.ts';

import { getTranslatedError, parseError, isUnexpectedError } from './inlineCommentsErrorUtils.ts';
import { InlineCommentFramework } from './enum/InlineCommentFramework.tsx';
import { getTargetNodeType } from './analyticsUtils.ts';

export const handleResolveSuccess = ({
	commentId,
	parentCommentMarkerRef,
	pageId,
	pageMode,
	eventEmitter,
	source,
	commentType,
	removeUnresolvedInlineComment,
	createAnalyticsEvent,
	getInlineNodeTypes,
	setEditorViewProps,
}: {
	commentId?: string;
	parentCommentMarkerRef: string;
	pageId: string;
	pageMode: PageMode;
	commentType: CommentType;
	eventEmitter?: AnnotationUpdateEmitter;
	source: AnalyticsSource;
	removeUnresolvedInlineComment?: (unresolvedAnnotationId: string | null) => void;
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent;
	getInlineNodeTypes?: (annotationId: string) => string[] | undefined;
	setEditorViewProps?: (viewProps: null) => void;
}): void => {
	// Update the unresolved inline comments count
	removeUnresolvedInlineComment && removeUnresolvedInlineComment(parentCommentMarkerRef);

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'resolved',
			actionSubject: 'comment',
			actionSubjectId: commentId,
			objectType: 'page',
			objectId: pageId,
			source,
			attributes: {
				...getCommentsDataManagerExperimentAttributes(),
				source,
				commentType,
				pageMode,
				framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				navdexPointType: END,
				inlineNodeTypes:
					parentCommentMarkerRef && getInlineNodeTypes
						? getInlineNodeTypes(parentCommentMarkerRef)
						: undefined,
				targetNodeType: getTargetNodeType(parentCommentMarkerRef, pageMode !== PageMode.VIEW),
			},
		},
	}).fire();

	// Emit the event to mark the annotation as resolved
	if (pageMode === PageMode.VIEW) {
		eventEmitter?.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
			[parentCommentMarkerRef]: {
				id: parentCommentMarkerRef,
				annotationType: AnnotationTypes.INLINE_COMMENT,
				state: AnnotationMarkStates.RESOLVED,
			},
		});
	} else {
		eventEmitter?.emit('resolve', parentCommentMarkerRef);
		setEditorViewProps && setEditorViewProps(null);
	}
};

export const handleDeleteSuccess = ({
	commentId,
	annotationId,
	pageId,
	pageMode,
	isReply,
	source,
	inlineNodeTypes,
	removeUnresolvedInlineComment,
	createAnalyticsEvent,
	onSuccess,
}: {
	commentId?: string;
	annotationId?: string;
	pageId: string;
	pageMode: PageMode;
	isReply: boolean;
	source: AnalyticsSource;
	inlineNodeTypes?: string[];
	createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent;
	removeUnresolvedInlineComment: (unresolvedAnnotationId: string | null) => void;
	onSuccess: (annotationId: string) => void;
}): void => {
	if (!annotationId) {
		throw new Error('No annotationId for delete');
	}

	// Update the unresolved inline comments count
	removeUnresolvedInlineComment(annotationId);

	createAnalyticsEvent({
		type: 'sendTrackEvent',
		data: {
			action: 'deleted',
			actionSubject: 'comment',
			actionSubjectId: commentId,
			objectType: 'page',
			objectId: pageId,
			source,
			attributes: {
				...getCommentsDataManagerExperimentAttributes(),
				commentType: CommentType.INLINE,
				source,
				pageMode,
				framework: InlineCommentFramework.ANNOTATION_PROVIDER,
				inlineNodeTypes,
				isReply,
				targetNodeType: getTargetNodeType(annotationId, pageMode !== PageMode.VIEW),
			},
		},
	}).fire();

	// Call the function passed in to do success specific things for each iteration
	onSuccess(annotationId);
};

// TODO: Move me and the translation logic to a shared component now that both
// general and inline comments use this logic to handle mutation failures
export const handleMutationFailure = ({
	experienceTracker,
	experienceName,
	error,
	commentId,
	shouldReturnError = false,
	shouldAbortForExpectedError = false,
	compoundExperienceName,
}: {
	experienceTracker: ExperienceTrackerAPI;
	experienceName: string;
	error: Error;
	commentId?: string;
	shouldReturnError?: boolean;
	shouldAbortForExpectedError?: boolean;
	compoundExperienceName?: string;
}): Promise<never> | undefined => {
	// get a truncated graphql error message
	const { message } = parseError(error);
	const translatedError = getTranslatedError(message, commentId);

	if (isUnexpectedError(translatedError)) {
		experienceTracker.stopOnError({
			name: experienceName,
			error,
		});

		/*
		 * stopOnError will fail the experience unless it's a session or network error, then it will be aborted.
		 * If the experience is failed and is a sub-experience, the compound experience will be failed automatically.
		 * If the experience is aborted and is a sub-experience, we need to abort the compound experience.
		 */
		if (compoundExperienceName) {
			const experienceState = experienceTracker.getExperienceState(experienceName);

			if (experienceState?.stopState === ExperienceStopStates.TASK_ABORT) {
				experienceTracker.stopOnError({
					name: compoundExperienceName,
					error,
				});
			}
		}
	} else {
		// we currently display a generic message to the user whenever an error occurs
		// These errors will not be reported to splunk/signalfx through experience tracking as they are expected
		markErrorAsHandled(error);

		if (shouldAbortForExpectedError) {
			// Ensure reason is always a string
			const reason =
				typeof translatedError.defaultMessage === 'string'
					? translatedError.defaultMessage
					: message;

			experienceTracker.abort({
				name: experienceName,
				reason,
			});

			// If a compound experience is also running, we need to abort it
			if (compoundExperienceName) {
				experienceTracker.abort({
					name: compoundExperienceName,
					reason,
				});
			}
		}
	}

	if (shouldReturnError) {
		// CommentEditor expects a translated error message otherwise it fails
		// TODO: CommentEditor should likely just receive the translated message
		// rather than expect the object needed for FormattedMessage
		return Promise.reject({
			error: translatedError,
		});
	}
};
